import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { buildDataSummary, type ColumnMeta, type Row } from '@/lib/biData';
import AiRichContent from '@/components/feature/AiRichContent';

// ============================================================
// Zifek BI — chatbot IA sur les données.
// L'utilisateur interroge ses données en langage naturel ; l'IA
// répond à partir du résumé réel des données (aucun chiffre inventé).
// ============================================================

interface ChatMessage { role: 'user' | 'assistant'; content: string; }

const SUGGESTIONS = [
  'Quelles sont les tendances principales ?',
  'Quelle est la valeur la plus élevée ?',
  'Quel est le total de la colonne principale ?',
  'Y a-t-il des anomalies dans les données ?',
];

export default function BiChat({
  columns,
  rows,
  datasetName,
}: {
  columns: ColumnMeta[];
  rows: Row[];
  datasetName: string;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const send = async (question: string) => {
    const q = question.trim();
    if (!q || loading) return;
    const next: ChatMessage[] = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const summary = buildDataSummary(datasetName, columns, rows);
      const { data, error } = await supabase.functions.invoke('bi-ai', {
        body: { mode: 'chat', summary, messages: next, question: q },
      });
      if (error) throw error;
      const reply = data?.reply || data?.error || "Je n'ai pas pu répondre.";
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', content: `Erreur : ${err instanceof Error ? err.message : 'inconnue'}` }]);
    } finally {
      setLoading(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col h-[560px]">
      <div className="px-4 py-3 border-b border-background-200/70 flex items-center gap-2">
        <i className="ri-robot-2-line text-primary-500"></i>
        <span className="text-sm font-semibold text-foreground-900">Assistant Zifek BI</span>
        <span className="ml-auto text-[11px] text-foreground-400 truncate max-w-[200px]">Source : {datasetName}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <i className="ri-question-answer-line text-3xl text-foreground-300 block mb-2"></i>
            <p className="text-sm text-foreground-600 font-medium">Posez une question sur vos données</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary-500 text-background-50 rounded-br-sm whitespace-pre-wrap' : 'bg-background-100 text-foreground-800 rounded-bl-sm'}`}>
              {m.role === 'user' ? m.content : <AiRichContent content={m.content} />}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-2xl bg-background-100 text-foreground-500 text-sm">
              <i className="ri-loader-4-line animate-spin mr-1"></i>Analyse en cours…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-background-200/70 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void send(input); } }}
          placeholder="Ex : montre-moi les ventes par mois…"
          className="flex-1 px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
        />
        <button onClick={() => void send(input)} disabled={loading || !input.trim()} className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer flex-shrink-0">
          <i className="ri-send-plane-2-line"></i>
        </button>
      </div>
    </div>
  );
}